import React, { useMemo, useState } from 'react';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

const jobs = [
  {
    name: 'UniverseBuilderJob',
    label: 'Universe Builder',
    api: '/api/admin/job/universe-builder',
    description: 'Reconstruit l univers de travail et les actifs suivis par le cockpit.',
    icon: <PrecisionManufacturingRoundedIcon />,
  },
  {
    name: 'AssetScannerJob',
    label: 'Asset Scanner',
    api: '/api/admin/job/asset-scanner',
    description: 'Relance le scan principal pour recalculer les opportunites et signaux.',
    icon: <AutoGraphRoundedIcon />,
  },
  {
    name: 'PatternScannerJob',
    label: 'Pattern Scanner',
    api: '/api/admin/job/pattern-scanner',
    description: 'Declenche l analyse des patterns techniques sur l univers courant.',
    icon: <TimelineRoundedIcon />,
  },
  {
    name: 'CryptoPriceHistoryJob',
    label: 'Historique des prix crypto',
    api: '/api/admin/job/crypto-price-history',
    description: 'Met a jour l historique de prix utile aux calculs et vues retroactives.',
    icon: <SavingsRoundedIcon />,
  },
  {
    name: 'SimulatePriceHistory',
    label: 'Simuler 60 jours de prix',
    api: '/api/admin/job/simulate-price-history',
    description: 'Genere un jeu de donnees de prix pour enrichir un environnement de dev.',
    icon: <PlayArrowRoundedIcon />,
  },
];

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(247,243,234,0.12)',
        minWidth: 132,
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(247,243,234,0.72)', display: 'block', mb: 0.35 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  );
}

const JobAdminPage = () => {
  const [loadingJob, setLoadingJob] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastRun, setLastRun] = useState(null);

  const idle = useMemo(() => !loadingJob, [loadingJob]);

  const handleRunJob = async (api) => {
    const currentJob = jobs.find((job) => job.api === api);
    setLoadingJob(currentJob?.name || api);
    setError('');
    setSuccessMessage('');
    try {
      const res = await fetch(api, { method: 'POST' });
      const payload = await res.json().catch(() => null);
      if (!res.ok || payload?.status === 'FAILED') {
        throw new Error(payload?.message || 'Erreur lors du déclenchement du job');
      }
      const message = payload?.message || 'Job declenche avec succes';
      setSuccessMessage(message);
      setLastRun({
        label: currentJob?.label || 'Job admin',
        message,
        at: new Date().toLocaleString('fr-FR'),
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingJob('');
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Jobs" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Declencher les traitements manuels de l'administration
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page permet de lancer les jobs backend a la demande pour reconstruire l'univers, rescanner les actifs, recalculer les patterns ou remplir l'historique de prix.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <StatBadge label="Jobs disponibles" value={`${jobs.length}`} accent="#f7f3ea" />
              <StatBadge label="Etat" value={idle ? 'IDLE' : 'RUNNING'} accent={idle ? '#8fd6b1' : '#f3c65c'} />
              <StatBadge label="Dernier lancement" value={lastRun?.label || 'Aucun'} accent={lastRun ? '#f3c65c' : '#f7f3ea'} />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 1.15, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Actions disponibles
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Chaque bouton declenche directement l'endpoint admin correspondant. L'execution backend continue ensuite selon la logique du job appele.
            </Typography>

            <Stack spacing={1.8}>
              {jobs.map((job) => {
                const isRunning = loadingJob === job.name;
                return (
                  <Paper
                    key={job.name}
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 4,
                      background: '#fff',
                      border: '1px solid rgba(18,44,52,0.08)',
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#f6f1e5',
                            color: '#17323c',
                            flexShrink: 0,
                          }}
                        >
                          {job.icon}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#19323c', mb: 0.35 }}>
                            {job.label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#4f646f' }}>
                            {job.description}
                          </Typography>
                        </Box>
                      </Stack>

                      <Button
                        variant={isRunning ? 'outlined' : 'contained'}
                        onClick={() => handleRunJob(job.api)}
                        disabled={!idle}
                        startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : <PlayArrowRoundedIcon />}
                        sx={{
                          minHeight: 48,
                          minWidth: 180,
                          borderRadius: 99,
                          bgcolor: isRunning ? 'transparent' : '#17323c',
                          color: isRunning ? '#17323c' : '#f7f3ea',
                          borderColor: '#17323c',
                          ':hover': isRunning ? { borderColor: '#17323c' } : { bgcolor: '#0f2329' },
                        }}
                      >
                        {isRunning ? 'Execution...' : `Lancer ${job.label}`}
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>

          <Paper sx={{ flex: 0.92, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Etat et retour
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Les retours de l'API admin sont affiches ici pour eviter les popups navigateur et garder une lecture coherente avec le reste du back-office.
            </Typography>

            <Stack spacing={2}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Regles d'execution
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Un seul declenchement est permis a la fois depuis cette page. Lorsque le backend confirme l'appel, le message de succes ou d'erreur reste visible ici jusqu'au prochain lancement.
                </Typography>
              </Paper>

              {error && <Alert severity="error">{error}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} useFlexGap flexWrap="wrap">
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Job courant
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {loadingJob ? jobs.find((job) => job.name === loadingJob)?.label || loadingJob : 'Aucun'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Derniere action
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.label || 'Aucune'}
                  </Typography>
                </Paper>
                <Paper elevation={0} sx={{ px: 1.6, py: 1.4, borderRadius: 3, background: '#fcf8ef', border: '1px solid rgba(18,44,52,0.08)', minWidth: 132 }}>
                  <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
                    Horodatage
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#19323c', fontWeight: 800 }}>
                    {lastRun?.at || 'n/a'}
                  </Typography>
                </Paper>
              </Stack>

              <Paper elevation={0} sx={{ p: 2, borderRadius: 4, background: '#f8fbff', border: '1px solid rgba(18,44,52,0.08)' }}>
                <Typography variant="subtitle2" sx={{ color: '#19323c', fontWeight: 700, mb: 0.8 }}>
                  Dernier message
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f', lineHeight: 1.7 }}>
                  {lastRun?.message || 'Aucun job n a encore ete declenche depuis cette page.'}
                </Typography>
              </Paper>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

export default JobAdminPage;
